import React from 'react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BasketSelectionInterface from '../BasketSelectionInterface/BasketSelectionInterface';

const TopicActions = ({ isInBasket, onBasketChange, currentBasketId, topic, baskets }) => {
  const [showBasketSelection, setShowBasketSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const hasHardConcept = (topic?.concepts || []).some((c) => c.is_highlighted);
  const isBasketChangeBlocked = !hasHardConcept;

  const handleBasketActionClick = () => {
    if (isBasketChangeBlocked) return;
    setIsLoading(true);
    setShowBasketSelection(true);
  };

  const handleBasketSelected = (basketId) => {
    if (!hasHardConcept) {
      toast.error('Mark at least one detail as hard before changing baskets');
      setShowBasketSelection(false);
      setIsLoading(false);
      return;
    }
    onBasketChange(basketId);
    setShowBasketSelection(false);
    setIsLoading(false);
  };

  // Reset loading state once the basket selection interface is displayed
  useEffect(() => {
    if (showBasketSelection) {
      // Small delay to ensure the interface has rendered
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showBasketSelection]);

  const handleClose = () => {
    setShowBasketSelection(false);
    setIsLoading(false);
  };

  return (
    <>
      <footer className="fixed bottom-0 left-0 right-0 p-4 bg-neutral-50">
        <div className="max-w-6xl mx-auto">
          <button 
            type="button"
            className={`
              w-full rounded-xl py-3 font-semibold 
              transition-colors duration-300 flex items-center justify-center gap-2
              ${isBasketChangeBlocked
                ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                : 'bg-cranberry-700 text-white hover:bg-cranberry-600'}
              ${isLoading ? 'opacity-75 cursor-wait' : ''}
            `}
            onClick={handleBasketActionClick}
            disabled={isLoading || isBasketChangeBlocked}
            aria-disabled={isBasketChangeBlocked}
            title={isBasketChangeBlocked ? 'Mark at least one detail as hard before adding or moving between baskets' : undefined}
            aria-label={
              isBasketChangeBlocked
                ? isInBasket
                  ? 'Move topic to another basket (mark at least one detail as hard first)'
                  : 'Add to a basket (mark at least one detail as hard first)'
                : isInBasket
                  ? 'Move topic to another basket'
                  : 'Add topic to a basket'
            }
          >
            {isLoading ? (
              <>
                <svg 
                  className="animate-spin h-5 w-5 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              isInBasket ? "Move to another basket" : "Add to a Basket"
            )}
          </button>
        </div>
      </footer>
      {showBasketSelection && (
        <BasketSelectionInterface
          onClose={handleClose}
          onBasketSelected={handleBasketSelected}
          currentBasketId={currentBasketId}
          topic={topic}
          workspaceId={topic.workspace_id}
        />
      )}
    </>
  );
};

export default TopicActions;
